<script>
import { useVuelidate } from '@vuelidate/core';
import { required, url } from '@vuelidate/validators';
import { useAlert } from 'dashboard/composables';

import NextButton from 'dashboard/components-next/button/Button.vue';
// NOTICE: KAPTION-INTEGRATION - Icon picker for dashboard apps
import IconGalleryPicker from 'dashboard/components/DashboardApp/IconGalleryPicker.vue';

export default {
  components: {
    NextButton,
    IconGalleryPicker,
  },
  props: {
    show: {
      type: Boolean,
      default: false,
    },
    mode: {
      type: String,
      default: 'create',
    },
    selectedAppData: {
      type: Object,
      default: () => ({}),
    },
  },
  emits: ['close'],
  setup() {
    return { v$: useVuelidate() };
  },
  validations: {
    app: {
      title: { required },
      content: {
        type: { required },
        url: { required, url },
      },
    },
  },
  data() {
    return {
      isLoading: false,
      // NOTICE: KAPTION-INTEGRATION
      app: {
        title: '',
        placement: 'main',
        content: {
          type: 'frame',
          url: '',
        },
        // NOTICE: KAPTION-INTEGRATION - Icon support for dashboard apps
        icon: {},
        // NOTICE: KAPTION-INTEGRATION - Full-screen dashboard apps
        isFullScreen: false,
        fullScreenUrl: '',
        // NOTICE: KAPTION-INTEGRATION - Per-app secret support
        secretMode: 'legacy',
      },
      // NOTICE: KAPTION-INTEGRATION - Per-app secret support
      revealedSecret: null,
      isRegeneratingSecret: false,
      isFetchingSecret: false,
    };
  },
  computed: {
    header() {
      return this.$t(`INTEGRATION_SETTINGS.DASHBOARD_APPS.${this.mode}.HEADER`);
    },
    submitButtonLabel() {
      return this.$t(
        `INTEGRATION_SETTINGS.DASHBOARD_APPS.${this.mode}.FORM_SUBMIT`
      );
    },
    // NOTICE: KAPTION-INTEGRATION - Per-app secret support
    isSecretCustom() {
      return this.app.secretMode === 'custom';
    },
    hasExistingSecret() {
      return (
        this.mode === 'UPDATE' &&
        this.selectedAppData?.secret_mode === 'custom' &&
        this.selectedAppData?.secret_preview
      );
    },
  },
  mounted() {
    if (this.mode === 'UPDATE' && this.selectedAppData) {
      this.app.title = this.selectedAppData.title;
      this.app.content = this.selectedAppData.content[0];
      // NOTICE: KAPTION-INTEGRATION
      this.app.placement = this.selectedAppData.placement || 'main';
      // NOTICE: KAPTION-INTEGRATION - Load existing icon
      this.app.icon = this.selectedAppData.icon || {};
      // NOTICE: KAPTION-INTEGRATION - Full-screen dashboard apps
      this.app.isFullScreen = this.selectedAppData.is_full_screen || false;
      this.app.fullScreenUrl = this.selectedAppData.full_screen_url || '';
      // NOTICE: KAPTION-INTEGRATION - Per-app secret support
      this.app.secretMode = this.selectedAppData.secret_mode || 'legacy';
    }
  },
  methods: {
    closeModal() {
      // Reset the data once closed
      // NOTICE: KAPTION-INTEGRATION
      this.app = {
        title: '',
        placement: 'main',
        content: { type: 'frame', url: '' },
        icon: {},
        isFullScreen: false,
        fullScreenUrl: '',
        secretMode: 'legacy',
      };
      this.revealedSecret = null;
      this.$emit('close');
    },
    // NOTICE: KAPTION-INTEGRATION - Per-app secret support
    async regenerateSecret() {
      if (
        !window.confirm(
          this.$t(
            'INTEGRATION_SETTINGS.DASHBOARD_APPS.FORM.SECRET_REGENERATE_CONFIRM'
          )
        )
      ) {
        return;
      }

      this.isRegeneratingSecret = true;
      try {
        const result = await this.$store.dispatch(
          'dashboardApps/regenerateSecret',
          this.selectedAppData.id
        );
        this.revealedSecret = result.secret;
        useAlert(
          this.$t('INTEGRATION_SETTINGS.DASHBOARD_APPS.UPDATE.API_SUCCESS')
        );
      } catch {
        useAlert(
          this.$t('INTEGRATION_SETTINGS.DASHBOARD_APPS.UPDATE.API_ERROR')
        );
      } finally {
        this.isRegeneratingSecret = false;
      }
    },
    // NOTICE: KAPTION-INTEGRATION - Per-app secret support
    async copyToClipboard(value) {
      try {
        await navigator.clipboard.writeText(value);
      } catch {
        // Fallback for older browsers
        const textArea = document.createElement('textarea');
        textArea.value = value;
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
      }
      useAlert(
        this.$t('INTEGRATION_SETTINGS.DASHBOARD_APPS.FORM.SECRET_COPIED')
      );
    },
    async copySecret() {
      if (!this.revealedSecret) return;
      await this.copyToClipboard(this.revealedSecret);
    },
    // Copy an existing custom secret without regenerating: fetch it from the
    // authorized secret endpoint, then place it on the clipboard.
    async copyExistingSecret() {
      this.isFetchingSecret = true;
      try {
        const result = await this.$store.dispatch(
          'dashboardApps/fetchSecret',
          this.selectedAppData.id
        );
        await this.copyToClipboard(result.secret);
      } catch {
        useAlert(
          this.$t('INTEGRATION_SETTINGS.DASHBOARD_APPS.UPDATE.API_ERROR')
        );
      } finally {
        this.isFetchingSecret = false;
      }
    },
    async submit() {
      try {
        this.v$.$touch();
        if (this.v$.$invalid) {
          return;
        }

        const action = this.mode.toLowerCase();
        // NOTICE: KAPTION-INTEGRATION - Use title as sidebar label
        const sidebarSettings =
          this.app.placement === 'sidebar' ? { label: this.app.title } : {};
        const payload = {
          title: this.app.title,
          placement: this.app.placement,
          content: [this.app.content],
          sidebar_settings: sidebarSettings,
          // NOTICE: KAPTION-INTEGRATION - Include icon in payload
          icon: this.app.icon,
          // NOTICE: KAPTION-INTEGRATION - Full-screen dashboard apps
          is_full_screen: this.app.isFullScreen,
          full_screen_url: this.app.isFullScreen ? this.app.fullScreenUrl : '',
          // NOTICE: KAPTION-INTEGRATION - Per-app secret support
          secret_mode: this.app.secretMode,
        };

        if (action === 'update') {
          payload.id = this.selectedAppData.id;
        }

        this.isLoading = true;
        await this.$store.dispatch(`dashboardApps/${action}`, payload);
        useAlert(
          this.$t(
            `INTEGRATION_SETTINGS.DASHBOARD_APPS.${this.mode}.API_SUCCESS`
          )
        );
        this.closeModal();
      } catch (err) {
        useAlert(
          this.$t(`INTEGRATION_SETTINGS.DASHBOARD_APPS.${this.mode}.API_ERROR`)
        );
      } finally {
        this.isLoading = false;
      }
    },
  },
};
</script>

<template>
  <woot-modal :show="show" :on-close="closeModal">
    <div class="flex flex-col h-auto overflow-auto">
      <woot-modal-header :header-title="header" />
      <form class="w-full" @submit.prevent="submit">
        <woot-input
          v-model="app.title"
          :class="{ error: v$.app.title.$error }"
          class="w-full"
          :label="$t('INTEGRATION_SETTINGS.DASHBOARD_APPS.FORM.TITLE_LABEL')"
          :placeholder="
            $t('INTEGRATION_SETTINGS.DASHBOARD_APPS.FORM.TITLE_PLACEHOLDER')
          "
          :error="
            v$.app.title.$error
              ? $t('INTEGRATION_SETTINGS.DASHBOARD_APPS.FORM.TITLE_ERROR')
              : null
          "
          data-testid="app-title"
          @input="v$.app.title.$touch"
          @blur="v$.app.title.$touch"
        />
        <woot-input
          v-model="app.content.url"
          :class="{ error: v$.app.content.url.$error }"
          class="w-full"
          :label="$t('INTEGRATION_SETTINGS.DASHBOARD_APPS.FORM.URL_LABEL')"
          :placeholder="
            $t('INTEGRATION_SETTINGS.DASHBOARD_APPS.FORM.URL_PLACEHOLDER')
          "
          :error="
            v$.app.content.url.$error
              ? $t('INTEGRATION_SETTINGS.DASHBOARD_APPS.FORM.URL_ERROR')
              : null
          "
          data-testid="app-url"
          @input="v$.app.content.url.$touch"
          @blur="v$.app.content.url.$touch"
        />
        <!-- NOTICE: KAPTION-INTEGRATION - Display location selector -->
        <div class="w-full pb-4">
          <label class="text-sm font-medium text-n-text-dark">
            {{ $t('INTEGRATION_SETTINGS.DASHBOARD_APPS.FORM.PLACEMENT_LABEL') }}
          </label>
          <div class="flex gap-4 mt-2">
            <label class="flex items-center gap-2">
              <input
                v-model="app.placement"
                type="radio"
                value="main"
                class="w-4 h-4"
              />
              <!-- NOTICE: KAPTION-INTEGRATION - Placement option for main tabs -->
              <span class="text-sm">{{
                $t('INTEGRATION_SETTINGS.DASHBOARD_APPS.FORM.PLACEMENT_MAIN')
              }}</span>
            </label>
            <label class="flex items-center gap-2">
              <input
                v-model="app.placement"
                type="radio"
                value="sidebar"
                class="w-4 h-4"
              />
              <!-- NOTICE: KAPTION-INTEGRATION - Placement option for sidebar -->
              <span class="text-sm">{{
                $t('INTEGRATION_SETTINGS.DASHBOARD_APPS.FORM.PLACEMENT_SIDEBAR')
              }}</span>
            </label>
          </div>
        </div>
        <!-- NOTICE: KAPTION-INTEGRATION - Full-screen toggle -->
        <div class="w-full pb-4">
          <label class="flex items-center gap-2 cursor-pointer">
            <input
              v-model="app.isFullScreen"
              type="checkbox"
              class="w-4 h-4"
              data-testid="app-full-screen-toggle"
            />
            <span class="text-sm font-medium text-n-text-dark">
              {{
                $t(
                  'INTEGRATION_SETTINGS.DASHBOARD_APPS.FORM.FULL_SCREEN_TOGGLE'
                )
              }}
            </span>
          </label>
          <p class="mt-1 text-xs text-n-slate-11">
            {{
              $t(
                'INTEGRATION_SETTINGS.DASHBOARD_APPS.FORM.FULL_SCREEN_TOGGLE_HELP'
              )
            }}
          </p>
        </div>
        <!-- NOTICE: KAPTION-INTEGRATION - Full-screen URL (shown when toggle is on) -->
        <woot-input
          v-if="app.isFullScreen"
          v-model="app.fullScreenUrl"
          class="w-full"
          :label="
            $t('INTEGRATION_SETTINGS.DASHBOARD_APPS.FORM.FULL_SCREEN_URL_LABEL')
          "
          :placeholder="
            $t(
              'INTEGRATION_SETTINGS.DASHBOARD_APPS.FORM.FULL_SCREEN_URL_PLACEHOLDER'
            )
          "
          data-testid="app-full-screen-url"
        />
        <!-- NOTICE: KAPTION-INTEGRATION - Icon picker -->
        <IconGalleryPicker
          v-model="app.icon"
          :app-title="app.title"
          class="w-full pb-4"
        />
        <!-- NOTICE: KAPTION-INTEGRATION - Per-app secret support -->
        <div class="w-full pb-4">
          <label class="text-sm font-medium text-n-text-dark">
            {{
              $t('INTEGRATION_SETTINGS.DASHBOARD_APPS.FORM.SECRET_MODE_LABEL')
            }}
          </label>
          <p class="mt-1 mb-2 text-xs text-n-slate-11">
            {{
              $t('INTEGRATION_SETTINGS.DASHBOARD_APPS.FORM.SECRET_MODE_HELP')
            }}
          </p>
          <div class="flex gap-4 mt-2">
            <label class="flex items-center gap-2">
              <input
                v-model="app.secretMode"
                type="radio"
                value="legacy"
                class="w-4 h-4"
              />
              <span class="text-sm">{{
                $t(
                  'INTEGRATION_SETTINGS.DASHBOARD_APPS.FORM.SECRET_MODE_LEGACY'
                )
              }}</span>
            </label>
            <label class="flex items-center gap-2">
              <input
                v-model="app.secretMode"
                type="radio"
                value="custom"
                class="w-4 h-4"
              />
              <span class="text-sm">{{
                $t(
                  'INTEGRATION_SETTINGS.DASHBOARD_APPS.FORM.SECRET_MODE_CUSTOM'
                )
              }}</span>
            </label>
          </div>
        </div>
        <!-- NOTICE: KAPTION-INTEGRATION - Per-app secret: edit mode actions -->
        <div v-if="mode === 'UPDATE' && isSecretCustom" class="w-full pb-4">
          <!-- Revealed secret (after regeneration) -->
          <div v-if="revealedSecret" class="p-3 rounded-lg bg-n-alpha-black2">
            <label class="text-sm font-medium text-n-text-dark">
              {{
                $t(
                  'INTEGRATION_SETTINGS.DASHBOARD_APPS.FORM.SECRET_REVEALED_TITLE'
                )
              }}
            </label>
            <p class="mt-1 mb-2 text-xs text-n-slate-11">
              {{
                $t(
                  'INTEGRATION_SETTINGS.DASHBOARD_APPS.FORM.SECRET_REVEALED_HELP'
                )
              }}
            </p>
            <div class="flex items-center gap-2">
              <code class="flex-1 px-2 py-1 text-sm rounded bg-n-slate-3">{{
                revealedSecret
              }}</code>
              <NextButton
                faded
                sm
                slate
                :label="
                  $t('INTEGRATION_SETTINGS.DASHBOARD_APPS.FORM.SECRET_COPY_BTN')
                "
                @click.prevent="copySecret"
              />
            </div>
          </div>
          <!-- Existing secret preview with copy + regenerate -->
          <div v-else-if="hasExistingSecret" class="flex items-center gap-2">
            <code class="flex-1 px-2 py-1 text-sm rounded bg-n-slate-3">{{
              selectedAppData.secret_preview
            }}</code>
            <NextButton
              faded
              sm
              slate
              :label="
                $t('INTEGRATION_SETTINGS.DASHBOARD_APPS.FORM.SECRET_COPY_BTN')
              "
              :is-loading="isFetchingSecret"
              @click.prevent="copyExistingSecret"
            />
            <NextButton
              faded
              sm
              ruby
              :label="
                $t(
                  'INTEGRATION_SETTINGS.DASHBOARD_APPS.FORM.SECRET_REGENERATE_BTN'
                )
              "
              :is-loading="isRegeneratingSecret"
              @click.prevent="regenerateSecret"
            />
          </div>
          <!-- No secret yet, offer to generate -->
          <NextButton
            v-else
            faded
            sm
            :label="
              $t(
                'INTEGRATION_SETTINGS.DASHBOARD_APPS.FORM.SECRET_REGENERATE_BTN'
              )
            "
            :is-loading="isRegeneratingSecret"
            @click.prevent="regenerateSecret"
          />
        </div>
        <div class="flex flex-row justify-end w-full gap-2 px-0 py-2">
          <NextButton
            faded
            slate
            type="reset"
            :label="
              $t('INTEGRATION_SETTINGS.DASHBOARD_APPS.CREATE.FORM_CANCEL')
            "
            @click.prevent="closeModal"
          />
          <NextButton
            type="submit"
            :label="submitButtonLabel"
            :disabled="v$.$invalid"
            :is-loading="isLoading"
          />
        </div>
      </form>
    </div>
  </woot-modal>
</template>
